/**
 * GemsMesh — renders gemstones placed on the active ring mesh.
 *
 * Setting priority:
 *   1. STARFIELD layout type
 *   2. FLUSH style type
 *   3. First available setting type
 *
 * Gem placement is fully asynchronous and rebuilds whenever ringMesh,
 * the active template, or ring measurements change.
 *
 * CSG approach: gem volumes are boolean-subtracted from the ring body geometry
 * (via three-bvh-csg) so gems are occluded by the ring at the back. The original
 * ring geometry is restored on unmount / style-mode change.
 */

import React, { useEffect, useRef, useState } from 'react';
import { BufferGeometry, Material, Mesh, ShaderMaterial } from 'three';
import type { ApiRingTemplate } from '../../types/api';
import { buildGemMeshes, GemPlacementResult, pickSettingType } from '../../lib/three/gemPlacement';
import { setGemDark, setGemGlowColor } from '../../lib/three/createGemMaterial';
import { setGemSpots } from '../../lib/materials/createRingPatternMaterial';
import useStore from '../../store/useStore';
import type CustomShaderMaterial from 'three-custom-shader-material/vanilla';

interface Props {
    ringMesh: Mesh;
    activeTemplate: ApiRingTemplate;
    ringSize: number;
    ringHeight: number;
    /** Ring pattern material — when provided, gem positions are pushed as shader uniforms
     *  so the fluorescent ring halo is painted on the ring surface. */
    patternMat?: CustomShaderMaterial | null;
}

const GemsMesh: React.FC<Props> = ({ ringMesh, activeTemplate, ringSize, ringHeight, patternMat }) => {
    const [result, setResult] = useState<GemPlacementResult | null>(null);
    const mountedRef = useRef(true);
    /** Original ring geometry before CSG — restored on cleanup / unmount. */
    const originalGeoRef = useRef<BufferGeometry | null>(null);
    /** Currently applied CSG-modified ring geometry. */
    const modifiedGeoRef = useRef<BufferGeometry | null>(null);
    /**
     * Tracks the last gem result that was handed to React state so we can
     * dispose it when a new result arrives or when the effect re-runs.
     *
     * NOT the same as `result` state: we intentionally avoid a separate
     * `useEffect([result])` for disposal because React StrictMode runs that
     * effect's cleanup immediately after setup (with the same captured value),
     * which would dispose the mesh right before it renders.
     */
    const prevResultRef = useRef<GemPlacementResult | null>(null);
    /**
     * The live gem material, so the dark-mode subscription always targets the
     * current instance (never a disposed one). The material is recreated on every
     * config change, so a subscribe alone would miss newly-built materials — each
     * fresh material is also seeded from the current `isDark` when it resolves.
     */
    const gemMatRef = useRef<ShaderMaterial | null>(null);

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    // Keep the live gem material's dark-mode emissive (on/off + selected glow colour)
    // in sync with the store. Captures only refs + module imports, so no
    // exhaustive-deps disable is needed.
    useEffect(() => {
        return useStore.subscribe(
            (s) => ({ isDark: s.isDark, glowColor: s.glowColor }),
            ({ isDark, glowColor }) => {
                if (!gemMatRef.current) return;
                setGemDark(gemMatRef.current, isDark);
                setGemGlowColor(gemMatRef.current, glowColor.threeHex);
            },
            { equalityFn: (a, b) => a.isDark === b.isDark && a.glowColor.id === b.glowColor.id },
        );
    }, []);

    useEffect(() => {
        const ringPartial = activeTemplate.ringPartials[0];
        const settingTypes = ringPartial?.settingTypes ?? [];
        const settingType = pickSettingType(settingTypes);

        // Gems come from settingType.allowedGems (not ring_partial.gems which is always empty)
        if (!settingType || !settingType.allowedGems?.length) {
            setResult(null);
            return;
        }

        let cancelled = false;

        buildGemMeshes({
            ringMesh,
            settingType,
            ringSize,
            ringHeight,
        }).then((r) => {
            if (cancelled || !mountedRef.current) {
                r?.gemMesh.geometry.dispose();
                (r?.gemMesh.material as Material | undefined)?.dispose();
                r?.modifiedRingGeometry.dispose();
                return;
            }

            // Dispose previous gem result now that a fresh one is ready.
            const prev = prevResultRef.current;
            if (prev) {
                prev.gemMesh.geometry.dispose();
                (prev.gemMesh.material as Material).dispose();
            }
            prevResultRef.current = r ?? null;

            // Track the live gem material and seed it with the current dark-mode
            // state immediately (mirrors PlaceholderRing's create-time apply), so a
            // freshly-built material glows right away when already in dark mode.
            const gemMat = (r?.gemMesh.material as ShaderMaterial | undefined) ?? null;
            gemMatRef.current = gemMat;
            if (gemMat) {
                const { isDark, glowColor } = useStore.getState();
                setGemDark(gemMat, isDark);
                setGemGlowColor(gemMat, glowColor.threeHex);
            }

            // Push gem positions to the ring pattern material so the shader can
            // darken the carved socket under each gem (the emissive halo around the
            // gem was removed — the gem itself now emits from inside the setting).
            if (patternMat && r) {
                setGemSpots(patternMat, r.gemPositions, r.gemRadii);
            }

            // Apply CSG-modified ring geometry so pockets are physically present.
            if (r?.modifiedRingGeometry) {
                if (!originalGeoRef.current) {
                    originalGeoRef.current = ringMesh.geometry;
                }
                modifiedGeoRef.current?.dispose();
                modifiedGeoRef.current = r.modifiedRingGeometry;
                ringMesh.geometry = r.modifiedRingGeometry;
            }

            setResult(r ?? null);
        }).catch((err: unknown) => {
            if (!cancelled && mountedRef.current) {
                console.error('[GemsMesh] placement failed:', err);
                setResult(null);
            }
        });

        return () => {
            cancelled = true;
            // Restore the original ring geometry so the ring body is clean for other modes.
            if (originalGeoRef.current) {
                modifiedGeoRef.current?.dispose();
                modifiedGeoRef.current = null;
                ringMesh.geometry = originalGeoRef.current;
                originalGeoRef.current = null;
            }
            // Dispose the active gem result (dep change or unmount).
            // Safe in StrictMode: before the async completes prevResultRef is null,
            // so the StrictMode cleanup is always a no-op for that ref.
            const prev = prevResultRef.current;
            if (prev) {
                prev.gemMesh.geometry.dispose();
                (prev.gemMesh.material as Material).dispose();
                prevResultRef.current = null;
            }
            gemMatRef.current = null;
            // Clear gem halo from the ring surface shader.
            if (patternMat) setGemSpots(patternMat, [], []);
            setResult(null);
        };
    }, [ringMesh, activeTemplate, ringSize, ringHeight, patternMat]);

    if (!result) return null;

    return <primitive object={result.gemMesh} />;
};

export default GemsMesh;

